import { useEffect, useState, FocusEvent, ChangeEvent, useCallback, useMemo, ReactNode } from 'react';
import { Field, useField } from 'react-final-form';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { Textarea, ITextareaProps } from 'UIPackage/MainModule/components/Textarea';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { Button } from 'UIPackage/MainModule/components/Button';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';

import { IAddressAutoCompleteOption } from '../interfaces/IAddressAutoCompleteOption';
import { useKnmAddresses } from '../hooks/useKnmAddresses';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import styles from './KnmAddressField.module.scss';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { getDistrictSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { findRegionSelectOption } from './regions';
import { getKnmDataCreated, getKnmSystemType } from '../selectors/knmSelectors';
import { NOT_IN_FIAS_TEXT } from '../../../CorePackage/MainModule/constants/notInFIASText';
import { getDecisionDataCreatedAt } from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';
import { ISelectObject, KnmObjectAddressModal, labelLocationObject } from './KnmObjectAddressModal';
import { KnmLateFieldsRemark } from '../../KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

export interface IKnmAddressFieldFieldsNames {
  fiasRegion?: string;
  notInFIAS?: string;
  manualAddress?: string;
  region?: string;
  // depretiated
  regionName?: string;
}

const INPUT_VALUE_MAX_LENGTH = 2048;

const defaultFieldNames: IKnmAddressFieldFieldsNames = {
  fiasRegion: 'fiasRegion',
  notInFIAS: 'notInFIAS',
  manualAddress: 'manualAddress',
  region: 'region',
  regionName: 'regionName',
};

interface IAddressFieldFieldProps {
  addressFieldPathWithPrefix: string;
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  fieldPaths?: IKnmAddressFieldFieldsNames;
  formRef?: ICommonFormRef;
  hideManualAddress?: boolean;
  hideNotInFiasCheckbox?: boolean;
  id: string;
  inputClassName?: string;
  inspectionResult?: IInspectionResult;
  isRegionTextField?: boolean;
  knmSystemType?: KnmSystemTypesEnum;
  manualAddressPlaceholder?: ReactNode;
  onAddressFieldBlur?: (value: string) => void;
  onClear?: () => void;
  placeholder?: ReactNode;
  prefixNameMatchesObjectAddress?: string;
  prefixNameNotInFias?: string;
  regionPlaceholder?: ReactNode;
  required?: boolean;
  resize?: ITextareaProps['resize'];
  trigger?: boolean;
  type?: 'row-field' | 'deleting-row';
  withWarning?: boolean;
  lateFieldName?: string;
}

interface IAddressFieldFieldStoreProps {
  created: string | null | undefined;
  decisionDataCreated?: string | null;
  districtSelectOptions: IKnmDistrictSelectOption[];
  isErp: boolean;
  settings: IAdminSetting[];
}

const KnmAddressFieldWithoutStore = ({
  addressFieldPathWithPrefix,
  className,
  clearable,
  disabled,
  fieldPaths,
  formRef,
  hideManualAddress,
  hideNotInFiasCheckbox,
  id,
  inputClassName,
  inspectionResult,
  isRegionTextField,
  knmSystemType,
  manualAddressPlaceholder = 'Адрес',
  onAddressFieldBlur,
  onClear,
  placeholder,
  prefixNameMatchesObjectAddress,
  prefixNameNotInFias,
  regionPlaceholder,
  resize,
  trigger,
  type,
  withWarning,
  created,
  decisionDataCreated,
  districtSelectOptions,
  isErp,
  settings,
  lateFieldName,
}: IAddressFieldFieldProps & IAddressFieldFieldStoreProps) => {
  const [addressOptions, setAddressOptions] = useState<IAddressAutoCompleteOption[] | undefined>();
  const [isFocus, setIsFocus] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabledFiasRegion, setIsDisabledFiasRegion] = useState(true);
  const [isShowPopupSelectObject, setIsShowPopupSelectObject] = useState(false);
  const [selectedObject, setSelectedObject] = useState<ISelectObject | undefined>(undefined);
  const change = formRef ? formRef?.current.change : () => {};
  const getFieldState = formRef ? formRef?.current.getFieldState : () => {};

  const finalFieldPaths: IKnmAddressFieldFieldsNames = useMemo(() => {
    return Object.fromEntries(
      Object.entries(defaultFieldNames).map(([key, defaultValue]) => [
        key,
        fieldPaths?.[key as keyof IKnmAddressFieldFieldsNames] ?? defaultValue,
      ]),
    ) as IKnmAddressFieldFieldsNames;
  }, [fieldPaths]);

  const addressFieldName = addressFieldPathWithPrefix;

  const notInFiasValue = useField(`${prefixNameNotInFias || ''}${finalFieldPaths.notInFIAS}`).input.value;
  const manualAddressValue = useField(`${prefixNameNotInFias || ''}${finalFieldPaths.manualAddress}`).input.value;
  const regionValue = useField(`${prefixNameNotInFias || ''}${finalFieldPaths.region}`).input.value;
  const fiasRegionValue = useField(`${prefixNameNotInFias || ''}${finalFieldPaths.fiasRegion}`).input.value;

  const addressValue = useField(addressFieldName, { subscription: { value: true } }).input.value;
  const matchesObjectAddressField = useField(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, {
    subscription: { value: true },
  });

  const objectsPmField = useField('objectsPm', {
    subscription: { value: true },
    allowNull: true,
  });
  const objectsErknmField = useField('objectsErknm', {
    subscription: { value: true },
    allowNull: true,
  });

  const isUse4123 = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_23_STARTS_FROM,
  );

  const isLaterThanHideFields4129 = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.SETTING_HIDE_FIELDS_4_1_29,
  );

  const matchesObjectAddressValue = prefixNameMatchesObjectAddress ? matchesObjectAddressField.input.value : undefined;

  const sortedRegionSelectOptions = useMemo(() => {
    return !districtSelectOptions || !Array.isArray(districtSelectOptions)
      ? []
      : [...districtSelectOptions].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ru'));
  }, [districtSelectOptions]);

  const { isSuccess: isAddressesSuccess, getAddresses, addresses } = useKnmAddresses();

  useEffect(() => {
    if (isAddressesSuccess) {
      setIsLoading(false);
      if (addresses.length > 0) {
        setAddressOptions(
          addresses.map(item => ({
            value: item,
            label: item,
          })),
        );
      } else {
        setAddressOptions(undefined);
      }
    }
  }, [addresses, isAddressesSuccess]);

  const findAddress = (value?: string) => {
    if (value && value.length > 2 && value.length <= INPUT_VALUE_MAX_LENGTH) {
      getAddresses(value).finally(() => setIsLoading(false));
    } else {
      setAddressOptions(undefined);
      setIsLoading(false);
    }
  };

  const debouncedGetAddresses = useCallback(debounce(findAddress, 1000), []);

  /**
   * Получение списка объектов контроля
   */
  const objects = useMemo(() => {
    if (knmSystemType === KnmSystemTypesEnum.PM) {
      return Array.isArray(objectsPmField.input.value) && objectsPmField.input.value.length > 0
        ? objectsPmField.input.value
        : [];
    }

    return Array.isArray(objectsErknmField.input.value) && objectsErknmField.input.value.length > 0
      ? objectsErknmField.input.value
      : [];
  }, [objectsPmField.input.value, objectsErknmField.input.value, knmSystemType]);

  const objectsOfControl = useMemo(() => {
    if (!objects.length) return [];

    return objects.map(i => {
      const result: ISelectObject = {};

      if (i.notInFIAS && i.manualAddress) {
        result.region = i.region;
        result.regionName = i.regionName;
        result.manualAddress = i.manualAddress;

        return result;
      }
      result.address = i.address;
      result.fiasRegion = i.fiasRegion;

      return result;
    });
  }, [objects]);
  /**
   * Функция изменения значения пользователем в галочке "Адрес совпадает с адресом объекта"
   */

  useEffect(() => {
    if (
      matchesObjectAddressValue &&
      prefixNameMatchesObjectAddress !== undefined &&
      prefixNameNotInFias !== undefined
    ) {
      change(`${prefixNameNotInFias}isErrorFiasValue`);
      if (objectsOfControl?.length === 1) {
        if (objectsOfControl[0].manualAddress) {
          change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`, objectsOfControl[0].manualAddress);
          change(`${prefixNameNotInFias}${finalFieldPaths.region}`, objectsOfControl[0].region);
          change(addressFieldName, undefined);
          change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`, objectsOfControl[0].region);
        } else {
          change(addressFieldName, objectsOfControl[0].address);
          change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`, objectsOfControl[0].fiasRegion);
        }

        if (!isLaterThanHideFields4129 && notInFiasValue) {
          if (objectsOfControl[0].address) {
            change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`, objectsOfControl[0].address);
            change(`${prefixNameNotInFias}${finalFieldPaths.region}`, objectsOfControl[0].region);
            change(addressFieldName, undefined);
            change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`, objectsOfControl[0].region);
          }
        }
      } else if (!objectsOfControl?.find(i => i.manualAddress === manualAddressValue) && manualAddressValue) {
        change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`);
        change(`${prefixNameNotInFias}${finalFieldPaths.region}`);
      }
    }
  }, [
    matchesObjectAddressValue,
    prefixNameMatchesObjectAddress,
    objectsOfControl,
    manualAddressValue,
    prefixNameNotInFias,
    notInFiasValue,
  ]);

  /**
   * Функция изменения выбранного местоположения объекта кнм
   */
  const onChangeSelectedLocationObject = useCallback(
    (object: ISelectObject) => {
      setSelectedObject(object);
      setIsError(false);
    },
    [prefixNameNotInFias],
  );

  const onAccept = useCallback(() => {
    if (!selectedObject) {
      setIsError(true);

      return null;
    }

    if (selectedObject.manualAddress) {
      change(`${prefixNameNotInFias}${finalFieldPaths.notInFIAS}`, true);
      change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`, selectedObject.manualAddress);
      change(`${prefixNameNotInFias}${finalFieldPaths.region}`, selectedObject.region);
      change(`${prefixNameNotInFias}${finalFieldPaths.regionName}`, selectedObject.regionName);
      change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`);
      change(addressFieldName, undefined);
    } else {
      change(`${prefixNameNotInFias}${finalFieldPaths.notInFIAS}`, false);
      change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`);
      change(`${prefixNameNotInFias}${finalFieldPaths.region}`);
      change(`${prefixNameNotInFias}${finalFieldPaths.regionName}`);
      change(addressFieldName, selectedObject.address);
      change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`, selectedObject.fiasRegion);
    }
    change(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, true);
    onClose();
  }, [prefixNameNotInFias, selectedObject, prefixNameMatchesObjectAddress, formRef]);

  const onClose = useCallback(() => {
    setSelectedObject(undefined);
    setIsError(false);
    setIsShowPopupSelectObject(false);
  }, []);

  /**
   * Функция сравнения выбранного местоположения объекта кнм
   */
  const isSelectedLocationObject = useCallback(
    (object: ISelectObject) => {
      const dataObj = {
        region: regionValue,
        manualAddress: manualAddressValue,
        address: addressValue,
        fiasRegion: fiasRegionValue,
      };

      return labelLocationObject(object) === labelLocationObject(selectedObject || dataObj);
    },
    [prefixNameNotInFias, regionValue, addressValue, fiasRegionValue, selectedObject],
  );

  /**
   * Функция изменения галочки нет в фаис
   */
  const onChangeNotInFias = () => {
    onAddressFieldBlur && onAddressFieldBlur('');
    change(addressFieldName);
    change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`);
    change(`${prefixNameNotInFias}${finalFieldPaths.region}`);
    change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`);
    change(`${prefixNameNotInFias}isErrorFiasValue`);

    if (!matchesObjectAddressValue && prefixNameMatchesObjectAddress !== undefined) {
      change(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, false);
      change(`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`);
      change(`${prefixNameNotInFias}${finalFieldPaths.region}`);
    }
  };

  /**
   * Функция открытия модалки с выбором
   */
  const openPopup = useCallback(() => {
    const dataObj = objectsOfControl?.find(obj => isSelectedLocationObject(obj));

    setSelectedObject(dataObj);
    setIsShowPopupSelectObject(true);
  }, [objectsOfControl]);

  /**
   * Функция изменения галочки "Адрес совпадает с адресом объекта"
   */
  const onChangeMatchesObjectAddress = useCallback(() => {
    if (!matchesObjectAddressValue) {
      openPopup();
    } else {
      change(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, false);
    }
  }, [
    openPopup,
    regionValue,
    manualAddressValue,
    addressValue,
    fiasRegionValue,
    formRef,
    prefixNameMatchesObjectAddress,
  ]);

  const errorPlaceText = getFieldState(addressFieldName)?.error;

  // Ряд ручного ввода скрыт для объектов из ЕРВК в паспортах после 4.1.29, адрес показывается в верхнем поле.
  // Если показывать нечего, ряд возвращаем, иначе manualAddress обязателен, а заполнить его негде
  const isManualAddressRowVisible =
    notInFiasValue &&
    (!hideManualAddress || !isLaterThanHideFields4129 || isErp || (!manualAddressValue && !addressValue));

  const isManualAddressShownInAddressField = isLaterThanHideFields4129 && notInFiasValue && !isManualAddressRowVisible;

  // До 4.1.29 субъект РФ переезжает в ряд с ручным адресом, поэтому в первом ряду его не рисуем.
  // Иначе на экране оказывается два одинаковых селекта, привязанных к одному полю формы
  const isRegionInManualAddressRow = !isLaterThanHideFields4129 && isManualAddressRowVisible;

  // Верхнее поле при notInFIAS отключено, значение только для показа.
  // Если address заполнен, показываем его, у старых объектов там лежит копия manualAddress
  const getDisplayedAddress = (value: string) => {
    if (!isLaterThanHideFields4129 && notInFiasValue) return undefined;
    if (isManualAddressShownInAddressField && !value) return manualAddressValue;

    return value;
  };

  const regionBlock = (
    <div className={styles.FIASRegion}>
      {isRegionTextField ? (
        <TextInputField
          className={styles.InputRegion}
          name={`${prefixNameNotInFias}${finalFieldPaths.regionName}`}
          placeholder="Субъект РФ"
          size="medium"
          disabled={true}
        />
      ) : (
        <SelectInputField
          className={isLaterThanHideFields4129 ? styles.InputRegion : styles.InputRegionNoMargin}
          options={sortedRegionSelectOptions}
          id={
            notInFiasValue
              ? `${prefixNameNotInFias}${finalFieldPaths.region}`
              : `${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`
          }
          name={
            notInFiasValue
              ? `${prefixNameNotInFias}${finalFieldPaths.region}`
              : `${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`
          }
          placeholder={regionPlaceholder || 'Субъект РФ'}
          size="medium"
          disabled={disabled || !!matchesObjectAddressValue || (!notInFiasValue && isDisabledFiasRegion)}
        />
      )}
    </div>
  );

  const addressBlock = (
    <div className={styles.addressBlock}>
      <Field id={id} name={addressFieldName}>
        {({ input, meta }) => {
          function handleAutoCompleteChange(e: ChangeEvent<HTMLTextAreaElement>) {
            const { value } = e.target;

            setIsLoading(true);
            if (value.trim().length === 0) {
              change(`${prefixNameNotInFias}isErrorFiasValue`);
            } else {
              change(`${prefixNameNotInFias}isErrorFiasValue`, true);
            }
            input.onChange(value);
            debouncedGetAddresses(value);
          }

          function handleAutoCompleteSelect(value: string) {
            return async () => {
              input.onChange(value);
              findAddress();
              setAddressOptions(undefined);
              onAddressFieldBlur && onAddressFieldBlur(value);
              // Адрес выбран из подсказок ФИАС — ошибка «нет в справочнике» неактуальна,
              // даже если субъект РФ по строке не удалось сопоставить.
              change(`${prefixNameNotInFias}isErrorFiasValue`);
              change(`${prefixNameNotInFias}${finalFieldPaths.notInFIAS}`, false);

              const hasFoundRegion = await findRegionSelectOption(value, undefined, districtSelectOptions);

              if (hasFoundRegion) {
                change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`, hasFoundRegion);
                setIsDisabledFiasRegion(true);
              } else {
                change(`${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`);
                setIsDisabledFiasRegion(false);
              }
            };
          }

          function handleTextInputBlur(e: FocusEvent<HTMLTextAreaElement>) {
            input.onBlur(e);
            const tagName = (e?.relatedTarget as Element)?.tagName;
            const idElement = (e?.relatedTarget as Element)?.id;

            if (tagName !== 'UL' && idElement !== `${prefixNameNotInFias}${finalFieldPaths.notInFIAS}`) {
              if (!isUse4123) {
                findAddress();
                setAddressOptions(undefined);
                setIsFocus(false);
              }
              onAddressFieldBlur && onAddressFieldBlur(e.target.value);
            }
          }

          function onFocus() {
            setIsFocus(true);
          }

          return (
            <AutoComplete
              type={type}
              options={addressOptions}
              noFoundMessage="Значение отсутствует"
              handleAutoCompleteSelect={handleAutoCompleteSelect}
              isActive={isFocus}
            >
              <Textarea
                {...input}
                className={inputClassName}
                placeholder={placeholder}
                resize={resize}
                error={notInFiasValue || !meta.touched ? undefined : meta.error}
                value={getDisplayedAddress(input.value)}
                withWarning={withWarning}
                clearable={clearable}
                disabled={disabled || !!notInFiasValue || !!matchesObjectAddressValue}
                onChange={handleAutoCompleteChange}
                onBlur={handleTextInputBlur}
                onClear={onClear}
                onFocus={onFocus}
                inspectionResult={inspectionResult}
                triggerCalculateHeight={trigger}
              />
            </AutoComplete>
          );
        }}
      </Field>
      <div className={isErp ? styles.CheckboxErpWrapper : styles.CheckboxWrapper}>
        {prefixNameNotInFias !== undefined && !hideNotInFiasCheckbox && (
          <CheckboxField
            theme="system"
            type="checkbox"
            id={`${prefixNameNotInFias}${finalFieldPaths.notInFIAS}`}
            name={`${prefixNameNotInFias}${finalFieldPaths.notInFIAS}`}
            disabled={
              disabled ||
              isLoading ||
              (errorPlaceText !== NOT_IN_FIAS_TEXT && !notInFiasValue) ||
              matchesObjectAddressValue ||
              (!addressValue && !notInFiasValue)
            }
            label={'Иное указание места (нет в ФИАС)'}
            onChangeCallback={onChangeNotInFias}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={`${styles.MainWrapper} ${className}`}>
      <div className={styles.FiasRow}>
        {isLaterThanHideFields4129 ? (
          <>
            {regionBlock}
            {lateFieldName && (
              <KnmLateFieldsRemark
                lateFieldPath={notInFiasValue ? `${lateFieldName}/region` : `${lateFieldName}/fiasRegion`}
                fieldId={`${
                  notInFiasValue
                    ? `${prefixNameNotInFias}${finalFieldPaths.region}`
                    : `${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`
                }`}
              />
            )}
            {addressBlock}
          </>
        ) : (
          <>
            {addressBlock}
            {!isRegionInManualAddressRow && regionBlock}
            {!isRegionInManualAddressRow && lateFieldName && (
              <KnmLateFieldsRemark
                lateFieldPath={notInFiasValue ? `${lateFieldName}/region` : `${lateFieldName}/fiasRegion`}
                fieldId={`${
                  notInFiasValue
                    ? `${prefixNameNotInFias}${finalFieldPaths.region}`
                    : `${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`
                }`}
              />
            )}
          </>
        )}
      </div>
      {isManualAddressRowVisible && (
        <div className={styles.FiasRow}>
          {isRegionInManualAddressRow && regionBlock}
          {isRegionInManualAddressRow && lateFieldName && (
            <KnmLateFieldsRemark
              lateFieldPath={notInFiasValue ? `${lateFieldName}/region` : `${lateFieldName}/fiasRegion`}
              fieldId={`${
                notInFiasValue
                  ? `${prefixNameNotInFias}${finalFieldPaths.region}`
                  : `${prefixNameNotInFias}${finalFieldPaths.fiasRegion}`
              }`}
            />
          )}
          <TextInputField
            className={styles.addressBlock}
            id={`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`}
            name={`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`}
            placeholder={manualAddressPlaceholder}
            disabled={disabled || matchesObjectAddressValue}
          />
        </div>
      )}
      {((prefixNameMatchesObjectAddress && !isErp) || matchesObjectAddressValue) && (
        <div className={styles.CheckboxWrapper}>
          {prefixNameMatchesObjectAddress && !isErp && (
            <Checkbox
              theme="system"
              type="checkbox"
              id={`${prefixNameMatchesObjectAddress}matchesObjectAddress`}
              disabled={disabled}
              checked={matchesObjectAddressValue}
              label={'Адрес совпадает с адресом объекта'}
              onChange={onChangeMatchesObjectAddress}
            />
          )}
          {matchesObjectAddressValue && (
            <Button
              className={`shared-btn-add ${styles.ButtonChange}`}
              text="Изменить"
              variant="link"
              color="primary"
              type="button"
              disabled={disabled}
              onClick={openPopup}
            />
          )}
        </div>
      )}
      <KnmObjectAddressModal
        visible={isShowPopupSelectObject}
        isError={isError}
        disabled={disabled}
        manualAddressFieldId={`${prefixNameNotInFias}${finalFieldPaths.manualAddress}`}
        objectsOfControl={objectsOfControl}
        isSelected={isSelectedLocationObject}
        onSelect={onChangeSelectedLocationObject}
        onAccept={onAccept}
        onCancel={onClose}
        onClose={() => setIsShowPopupSelectObject(false)}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IAddressFieldFieldStoreProps => ({
  created: getKnmDataCreated(state),
  decisionDataCreated: getDecisionDataCreatedAt(state),
  districtSelectOptions: getDistrictSelectOptions(state),
  isErp: getKnmSystemType(state) === KnmSystemTypesEnum.ERP,
  settings: getAdminFetchSettings(state),
});

export const KnmAddressField = connect(mapStateToProps)(KnmAddressFieldWithoutStore);
